import { Download, FileText } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useBookings } from "@/hooks/useBookings";
import { useHosts } from "@/hooks/useHosts";
import { useUnits } from "@/hooks/useUnits";
import { useToast } from "@/hooks/use-toast";

// ─── CSV Utilities ─────────────────────────────────────────────────────────────

/**
 * Converts an array of objects to a CSV string.
 * Handles comma and quote escaping.
 */
function toCSV(rows: Record<string, unknown>[]): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const str = v == null ? "" : String(v);
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  const headerRow = headers.map(escape).join(",");
  const dataRows = rows.map((row) => headers.map((h) => escape(row[h])).join(","));
  return [headerRow, ...dataRows].join("\n");
}

/**
 * Triggers a browser download of the given CSV content.
 */
function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Chart colour palette ─────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  approved: "#22c55e",   // green-500
  completed: "#3b82f6",  // blue-500
  pending: "#f59e0b",    // amber-500
  blocked: "#ef4444",    // red-500
  cancelled: "#6b7280",  // gray-500
};

const PIE_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#6b7280"];

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Reports() {
  const { data: stats, isLoading } = useDashboardStats();
  const { data: bookings = [] } = useBookings();
  const { data: hosts = [] } = useHosts();
  const { data: units = [] } = useUnits();
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // ── Chart data ───────────────────────────────────────────────────────────────

  // Booking status bar-chart data
  const bookingStatusData = [
    { name: "Approved", value: stats?.approvedBookings ?? 0, status: "approved" },
    { name: "Completed", value: stats?.completedBookings ?? 0, status: "completed" },
    { name: "Pending", value: stats?.pendingBookings ?? 0, status: "pending" },
    { name: "Blocked", value: stats?.blockedBookings ?? 0, status: "blocked" },
  ].filter((d) => d.value > 0);

  // Night usage bar-chart data (top 10 by usage %)
  const nightUsageData = (stats?.nightUsage ?? [])
    .sort((a: any, b: any) => b.percent - a.percent)
    .slice(0, 10)
    .map((item: any) => ({
      unit: `Unit ${item.unit}`,
      used: item.used,
      remaining: Math.max(0, item.limit - item.used),
      limit: item.limit,
      percent: item.percent,
    }));

  // Unit STR approval pie data
  const unitApprovalData = [
    { name: "STR Approved", value: stats?.strApproved ?? 0 },
    { name: "Not Approved", value: (stats?.totalUnits ?? 0) - (stats?.strApproved ?? 0) },
  ].filter((d) => d.value > 0);

  // ── Export handlers ──────────────────────────────────────────────────────────

  const handleExportBookings = () => {
    if (!bookings.length) {
      toast({ title: "No data", description: "There are no bookings to export.", variant: "destructive" });
      return;
    }
    const rows = (bookings as any[]).map((b) => ({
      id: b.id,
      guest_name: b.guest_name,
      guest_email: b.guest_email ?? "",
      guest_phone: b.guest_phone ?? "",
      guest_count: b.guest_count ?? "",
      unit: b.unit?.unit_number ?? "",
      check_in: b.check_in,
      check_out: b.check_out,
      nights: b.nights,
      status: b.status,
      payment_status: b.payment_status ?? "unpaid",
      fee: b.fee ?? 0,
      created_at: b.created_at,
    }));
    downloadCSV(toCSV(rows), `bookings_${new Date().toISOString().split("T")[0]}.csv`);
    toast({ title: "Exported", description: `${rows.length} bookings downloaded.` });
  };

  const handleExportHosts = () => {
    if (!hosts.length) {
      toast({ title: "No data", description: "There are no hosts to export.", variant: "destructive" });
      return;
    }
    const rows = (hosts as any[]).map((h) => ({
      id: h.id,
      unit: h.unit?.unit_number ?? "",
      registration_number: h.registration_number ?? "",
      host_type: h.host_type ?? "",
      status: h.status,
      nights_used: h.nights_used ?? 0,
      night_limit: h.night_limit ?? 0,
      nights_remaining: (h.night_limit ?? 0) - (h.nights_used ?? 0),
      expires_at: h.expires_at ?? "",
      created_at: h.created_at,
    }));
    downloadCSV(toCSV(rows), `hosts_${new Date().toISOString().split("T")[0]}.csv`);
    toast({ title: "Exported", description: `${rows.length} host records downloaded.` });
  };

  const handleExportUnits = () => {
    if (!units.length) {
      toast({ title: "No data", description: "There are no units to export.", variant: "destructive" });
      return;
    }
    const rows = (units as any[]).map((u) => ({
      id: u.id,
      unit_number: u.unit_number,
      owner_name: u.owner_name ?? "",
      owner_email: u.owner_email ?? "",
      str_approved: u.str_approved ? "Yes" : "No",
      status: u.status,
      created_at: u.created_at,
    }));
    downloadCSV(toCSV(rows), `units_${new Date().toISOString().split("T")[0]}.csv`);
    toast({ title: "Exported", description: `${rows.length} units downloaded.` });
  };

  const handleExportSummary = () => {
    const rows = [
      { metric: "Total Units", value: stats?.totalUnits ?? 0 },
      { metric: "STR Approved Units", value: stats?.strApproved ?? 0 },
      { metric: "Active Hosts", value: stats?.activeHosts ?? 0 },
      { metric: "Total Bookings", value: stats?.totalBookings ?? 0 },
      { metric: "Approved Bookings", value: stats?.approvedBookings ?? 0 },
      { metric: "Blocked Bookings", value: stats?.blockedBookings ?? 0 },
      { metric: "Pending Bookings", value: stats?.pendingBookings ?? 0 },
      { metric: "Completed Bookings", value: stats?.completedBookings ?? 0 },
      { metric: "Compliance Rate (%)", value: stats?.complianceRate ?? "100.0" },
      { metric: "Report Generated At", value: new Date().toISOString() },
    ];
    downloadCSV(toCSV(rows), `summary_report_${new Date().toISOString().split("T")[0]}.csv`);
    toast({ title: "Exported", description: "Summary report downloaded." });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground">Generate and export compliance reports</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportSummary}>
          <Download className="mr-1 h-4 w-4" />
          Export Summary CSV
        </Button>
      </div>

      {/* ── Row 1: Booking status + Unit approval ── */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

        {/* Booking Status Distribution — Bar Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Booking Status</CardTitle>
                <CardDescription className="text-xs">Distribution across all bookings</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleExportBookings}>
                <FileText className="mr-1 h-3 w-3" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {bookingStatusData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No booking data available.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={bookingStatusData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: 12,
                      color: "hsl(var(--popover-foreground))",
                    }}
                    cursor={{ fill: "hsl(var(--muted))" }}
                  />
                  <Bar dataKey="value" name="Bookings" radius={[4, 4, 0, 0]}>
                    {bookingStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLORS[entry.status] ?? "#94a3b8"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            {/* Legend */}
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              {bookingStatusData.map((d) => (
                <div key={d.status} className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLORS[d.status] }} />
                  <span className="text-muted-foreground">{d.name}</span>
                  <span className="font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Unit STR Approval — Pie Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Unit STR Approval</CardTitle>
                <CardDescription className="text-xs">Approved vs. non-approved units</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleExportUnits}>
                <FileText className="mr-1 h-3 w-3" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {unitApprovalData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No unit data available.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={unitApprovalData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {unitApprovalData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: 12,
                      color: "hsl(var(--popover-foreground))",
                    }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => (
                      <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
            {/* Stat row */}
            <div className="mt-2 flex justify-around text-center text-xs">
              <div>
                <p className="text-lg font-bold text-green-600">{stats?.strApproved ?? 0}</p>
                <p className="text-muted-foreground">Approved</p>
              </div>
              <div>
                <p className="text-lg font-bold">{(stats?.totalUnits ?? 0) - (stats?.strApproved ?? 0)}</p>
                <p className="text-muted-foreground">Not Approved</p>
              </div>
              <div>
                <p className="text-lg font-bold">{stats?.activeHosts ?? 0}</p>
                <p className="text-muted-foreground">Active Hosts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 2: Summary text stats ── */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Booking Summary</CardTitle>
                <CardDescription className="text-xs">Overview of all bookings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Bookings</span>
                <span className="font-medium">{stats?.totalBookings ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Approved</span>
                <span className="font-medium text-green-600">{stats?.approvedBookings ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Blocked</span>
                <span className="font-medium text-destructive">{stats?.blockedBookings ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pending</span>
                <span className="font-medium text-yellow-600">{stats?.pendingBookings ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Completed</span>
                <span className="font-medium">{stats?.completedBookings ?? 0}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground">Compliance Rate</span>
                <span className="font-semibold text-green-600">{stats?.complianceRate ?? "100.0"}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Host Summary</CardTitle>
                <CardDescription className="text-xs">Registration and activity overview</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleExportHosts}>
                <FileText className="mr-1 h-3 w-3" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Units</span>
                <span className="font-medium">{stats?.totalUnits ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">STR Approved</span>
                <span className="font-medium text-green-600">{stats?.strApproved ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Hosts</span>
                <span className="font-medium">{stats?.activeHosts ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 3: Night Usage — Stacked Bar Chart ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Night Usage by Host</CardTitle>
          <CardDescription className="text-xs">Annual night consumption per unit (used vs. remaining)</CardDescription>
        </CardHeader>
        <CardContent>
          {nightUsageData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No host data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, nightUsageData.length * 40)}>
              <BarChart
                data={nightUsageData}
                layout="vertical"
                margin={{ top: 4, right: 60, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="unit"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                    fontSize: 12,
                    color: "hsl(var(--popover-foreground))",
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} nights`,
                    name === "used" ? "Nights Used" : "Nights Remaining",
                  ]}
                />
                <Bar dataKey="used" name="used" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]}>
                  {nightUsageData.map((entry: any, index: number) => {
                    const pct = entry.percent;
                    const color = pct >= 95 ? "#ef4444" : pct >= 75 ? "#f59e0b" : "#22c55e";
                    return <Cell key={`used-${index}`} fill={color} />;
                  })}
                </Bar>
                <Bar dataKey="remaining" name="remaining" stackId="a" fill="hsl(var(--muted))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-green-500" /><span>Under 75%</span></div>
            <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-amber-500" /><span>75–94% (warning)</span></div>
            <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-red-500" /><span>95%+ (critical)</span></div>
            <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-muted" style={{ background: "hsl(var(--muted))" }} /><span>Remaining</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
